import React from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Link, Unlink, Hand, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DrawingTool } from './types';

interface GridSectionToolbarProps {
  imageId: string;
  zoom: number;
  activeTool?: DrawingTool;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomChange: (zoom: number) => void;
  onResetPosition: () => void;
  onToolChange?: (tool: DrawingTool) => void;
  showLinkControl?: boolean;
  isLinked?: boolean;
  onLinkToggle?: () => void;
}

const zoomLevels = [0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4, 5];

export const GridSectionToolbar: React.FC<GridSectionToolbarProps> = ({
  imageId,
  zoom,
  activeTool = 'pan',
  onZoomIn,
  onZoomOut,
  onZoomChange,
  onResetPosition,
  onToolChange,
  showLinkControl = false,
  isLinked = false,
  onLinkToggle
}) => {
  const currentZoomPercent = Math.round(zoom * 100);

  return (
    <div className="flex items-center justify-center gap-1 h-full">
      {/* Zoom Controls */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onZoomOut}
        className="h-6 w-6 p-0"
        title="Zoom Out"
      >
        <ZoomOut className="h-3 w-3" />
      </Button>
      
      <Select
        value={currentZoomPercent.toString()}
        onValueChange={(value) => onZoomChange(parseInt(value) / 100)}
      >
        <SelectTrigger className="h-6 w-20 text-xs">
          <SelectValue>{currentZoomPercent}%</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {zoomLevels.map((level) => (
            <SelectItem key={level} value={(Math.round(level * 100)).toString()}>
              {Math.round(level * 100)}%
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant="ghost"
        size="sm"
        onClick={onZoomIn}
        className="h-6 w-6 p-0"
        title="Zoom In"
      >
        <ZoomIn className="h-3 w-3" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={onResetPosition}
        className="h-6 w-6 p-0"
        title="Reset Position"
      >
        <RotateCcw className="h-3 w-3" />
      </Button>

      {/* Separator before drawing tools */}
      {onToolChange && <div className="w-px h-6 bg-border/50 mx-1"></div>}
      
      {/* Drawing Tools */}
      {onToolChange && (
        <>
          <Button
            variant={activeTool === 'rectangle' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onToolChange('rectangle')}
            className="h-6 w-6 p-0"
            title="Rectangle Tool"
          >
            <Square className="h-3 w-3" />
          </Button>
          
          <Button
            variant={activeTool === 'pan' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onToolChange('pan')}
            className="h-6 w-6 p-0"
            title="Pan Tool"
          >
            <Hand className="h-3 w-3" />
          </Button>
        </>
      )}
      {showLinkControl && onLinkToggle && (
        <Button
          variant={isLinked ? "default" : "ghost"}
          size="sm"
          onClick={onLinkToggle}
          className="h-6 w-6 p-0"
          title={isLinked ? "Unlink zoom and pan" : "Link zoom and pan"}
        >
          {isLinked ? <Link className="h-3 w-3" /> : <Unlink className="h-3 w-3" />}
        </Button>
      )}
    </div>
  );
};